import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { Layout, Menu, Avatar, Typography, Button, Popconfirm } from 'antd';
import type { MenuProps } from 'antd';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { useAuth } from '../contexts/AuthContext';
import PlatformAdminTopNavbar from './PlatformAdminTopNavbar';
import { STAFF_CONSOLE_LOGIN_PATH } from './staffConsolePaths';
import '../components/Sidebar.css';
import '../App.css';
import './platform-admin.css';

const { Sider } = Layout;
const { Text } = Typography;

const subMenuDotIcon = (
  <span className="submenu-dot" aria-hidden>
    <span className="submenu-dot-inner" />
  </span>
);

/**
 * 平台管理壳层：对齐实验室登录后布局（LabMind 侧栏 + 顶栏 + 内容区）。
 */
const PlatformAdminLayout: React.FC = () => {
  const { token, role, teamId, realName, username, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/platform-admin/feedback')) {
      setMenuOpenKeys(['feedback']);
    } else {
      setMenuOpenKeys((prev) => prev.filter((k) => k !== 'feedback'));
    }
  }, [location.pathname]);

  if (!token) {
    return <Navigate to={STAFF_CONSOLE_LOGIN_PATH} replace state={{ from: location }} />;
  }
  if (role !== '超级管理员' || teamId) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Text>
          仅「未绑定团队」的平台超级管理员可访问此处。若您属于某实验室团队，请从首页使用「进入系统」登录。
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button type="primary" onClick={() => navigate(STAFF_CONSOLE_LOGIN_PATH)}>
            重新登录
          </Button>
          <Button type="link" onClick={() => navigate('/')}>
            返回首页
          </Button>
        </div>
      </div>
    );
  }

  const path = location.pathname;
  const selectedKey = path.startsWith('/platform-admin/feedback/review')
    ? '/platform-admin/feedback/review'
    : path.startsWith('/platform-admin/feedback/analytics')
      ? '/platform-admin/feedback/analytics'
      : path.startsWith('/platform-admin/feedback/settings')
        ? '/platform-admin/feedback/settings'
        : path.startsWith('/platform-admin/unassigned-users')
          ? '/platform-admin/unassigned-users'
          : path.startsWith('/platform-admin/merchants')
            ? '/platform-admin/merchants'
            : '/platform-admin/teams';

  const toggleSidebar = () => setCollapsed((v) => !v);

  const handleLogout = () => {
    logout();
    navigate(STAFF_CONSOLE_LOGIN_PATH);
  };

  const go = (to: string) => {
    navigate(to);
    if (isMobile && !collapsed) toggleSidebar();
  };

  const menuItems: MenuProps['items'] = [
    {
      key: '/platform-admin/unassigned-users',
      icon: <LimsNavIcon name="person_search" sizePx={20} />,
      label: (
        <Link to="/platform-admin/unassigned-users" className="menu-link" onClick={() => isMobile && !collapsed && toggleSidebar()}>
          未归属用户
        </Link>
      )
    },
    {
      key: '/platform-admin/teams',
      icon: <LimsNavIcon name="apartment" sizePx={20} />,
      label: (
        <Link to="/platform-admin/teams" className="menu-link" onClick={() => isMobile && !collapsed && toggleSidebar()}>
          团队管理
        </Link>
      )
    },
    {
      key: '/platform-admin/merchants',
      icon: <LimsNavIcon name="storefront" sizePx={20} />,
      label: (
        <Link to="/platform-admin/merchants" className="menu-link" onClick={() => isMobile && !collapsed && toggleSidebar()}>
          商家管理
        </Link>
      )
    },
    {
      key: 'feedback',
      icon: <LimsNavIcon name="forum" sizePx={20} />,
      label: <span className="menu-link">匿名反馈治理</span>,
      children: [
        {
          key: '/platform-admin/feedback/review',
          icon: subMenuDotIcon,
          label: '内容审核',
          onClick: () => go('/platform-admin/feedback/review')
        },
        {
          key: '/platform-admin/feedback/analytics',
          icon: subMenuDotIcon,
          label: '数据看板',
          onClick: () => go('/platform-admin/feedback/analytics')
        },
        {
          key: '/platform-admin/feedback/settings',
          icon: subMenuDotIcon,
          label: '敏感词配置',
          onClick: () => go('/platform-admin/feedback/settings')
        }
      ]
    }
  ];

  return (
    <div className="app-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="antd-sidebar lims-sidebar"
        width={256}
        collapsedWidth={64}
      >
        {!collapsed && (
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <div className="lims-logo-mark lims-logo-mark--img" aria-hidden>
                <img
                  src={`${process.env.PUBLIC_URL || ''}/brand/labmind-logo.png`}
                  alt=""
                  className="lims-sidebar-logo-img"
                />
              </div>
              <div className="sidebar-brand-titles">
                <Text className="logo-text" strong>
                  LabMind
                </Text>
                <Text className="logo-tagline">Platform</Text>
              </div>
            </div>
            <Button
              type="text"
              icon={<LimsNavIcon name="left_panel_close" sizePx={20} />}
              onClick={toggleSidebar}
              className="sidebar-toggle"
            />
          </div>
        )}

        {collapsed && (
          <div className="collapsed-expand-button">
            <Button
              type="text"
              icon={<LimsNavIcon name="left_panel_open" sizePx={20} />}
              onClick={toggleSidebar}
              className="expand-toggle"
              size="large"
            />
          </div>
        )}

        <Menu
          theme="light"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          openKeys={collapsed ? [] : menuOpenKeys}
          onOpenChange={setMenuOpenKeys}
          className="sidebar-menu"
          items={menuItems}
        />

        {!collapsed && (
          <div className="sidebar-footer">
            <div className="user-info">
              <Avatar
                size={40}
                icon={<LimsNavIcon name="shield_person" sizePx={22} />}
                style={{
                  backgroundColor: 'rgba(0, 53, 95, 0.12)',
                  color: 'var(--lims-primary)'
                }}
              />
              <div className="user-details">
                <Text className="user-name" strong>
                  {realName || username || '管理员'}
                </Text>
                <Text className="user-role" type="secondary">
                  超级管理员
                </Text>
              </div>
              <Popconfirm
                title="确定要退出登录吗？"
                description="退出后将返回平台登录页"
                onConfirm={handleLogout}
                okText="确定"
                cancelText="取消"
                placement="topRight"
              >
                <Button
                  type="text"
                  icon={<LimsNavIcon name="logout" sizePx={18} />}
                  className="logout-button"
                  size="small"
                />
              </Popconfirm>
            </div>
          </div>
        )}
      </Sider>

      {isMobile && !collapsed && (
        <div className="sidebar-overlay" onClick={toggleSidebar} />
      )}

      <div className={`main-content ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <PlatformAdminTopNavbar onToggleSidebar={toggleSidebar} />
        <div className="page-container">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminLayout;
